import restApi from '@services/api';
import {} from '@type/util';
import { ApiTags } from '@constants/common';
import { GenericApiResponse } from '@type/general';

import {
  AddRoundReq,
  AddRoundRes,
  AddValuationReq,
  AddValuationRes,
  GetCompanyRes,
  Company,
  CreateCompanyReq,
  UpdateCompanyReq,
  CreateCompanyAddressReq,
  UpdateCompanyAddressReq,
  CreateCompanyEmailDomainRes,
  CreateCompanyEmailDomainReq,
  DeleteCompanyEmailDomainRes,
  BulkDeleteCompanyEmailDomainsReq,
  CreateTeamMemberReq,
  CreateTeamMemberRes,
  GetRoundsRes,
  TeamMemberListReq,
  TeamMemberRes,
  UpdateRoundReq,
  UpdateTeamMemberReq,
  UpdateValuationReq,
  GetInvestorRes,
  GetInvestorReq,
  Investor,
  CreateInvestorReq,
  CreateInvestorRes,
  UpdateInvestorRes,
  UpdateInvestorReq,
  DeleteInvestorReq,
  CompanyConfiguration,
  GetCompanyConfigurationRes,
  CreateCompanyConfigurationRes,
  CreateCompanyConfigurationReq,
  UpdateCompanyConfigurationReq,
  UpdateCompanyConfigurationRes,
  AddProductReq,
  AddProductAssetReq,
  UpdateProductReq,
  deleteProductAssetReq,
  addCompetitorReq,
  deleteCompetitorReq,
  updateCompetitorReq,
  deleteProductReq,
  addCompanyTermsReq
} from './types';

const CreateCompanyApi = restApi.injectEndpoints({
  endpoints: builder => ({
    getCompany: builder.query<Company, { companyId?: string }>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.companyId}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.getCompany],
      transformResponse: (response: GetCompanyRes) => response.result
    }),
    createCompany: builder.mutation<
      GenericApiResponse<{
        id: string;
      }>,
      CreateCompanyReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (
        response: GenericApiResponse<{
          id: string;
        }>
      ) => response
    }),
    updateCompany: builder.mutation<
      GenericApiResponse<string>,
      UpdateCompanyReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}`,
        method: 'PUT',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),

    getCompanyConfiguration: builder.query<
      CompanyConfiguration,
      { companyId?: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.companyId}/configuration`,
        method: 'GET'
      }),
      providesTags: [ApiTags.companyConfiguration],
      transformResponse: (response: GetCompanyConfigurationRes) =>
        response.result
    }),
    createCompanyConfiguration: builder.mutation<
      GenericApiResponse<CreateCompanyConfigurationRes>,
      CreateCompanyConfigurationReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.companyId}/configuration`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: res => (res ? [ApiTags.companyConfiguration] : []),
      transformResponse: (
        response: GenericApiResponse<CreateCompanyConfigurationRes>
      ) => response
    }),
    updateCompanyConfiguration: builder.mutation<
      GenericApiResponse<UpdateCompanyConfigurationRes>,
      UpdateCompanyConfigurationReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.companyId}/configuration/${payload.configurationId}`,
        method: 'PUT',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.companyConfiguration] : []),
      transformResponse: (
        response: GenericApiResponse<UpdateCompanyConfigurationRes>
      ) => response
    }),

    createCompanyAddress: builder.mutation<
      GenericApiResponse<string>,
      CreateCompanyAddressReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/address`,
        method: 'POST',
        body: payload.payload
      }),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    updateCompanyAddress: builder.mutation<
      GenericApiResponse<string>,
      UpdateCompanyAddressReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/address`,
        method: 'PUT',
        body: payload.payload
      }),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),

    createCompanyEmailDomain: builder.mutation<
      GenericApiResponse<string>,
      CreateCompanyEmailDomainReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/email-domain`,
        method: 'POST',
        body: payload.payload
      }),
      transformResponse: (response: CreateCompanyEmailDomainRes) => response
    }),
    bulkDeleteCompanyEmailDomains: builder.mutation<
      GenericApiResponse<string>,
      BulkDeleteCompanyEmailDomainsReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/email-domains`,
        method: 'POST',
        body: {
          ids: payload.email_domain_ids
        }
      }),
      transformResponse: (response: DeleteCompanyEmailDomainRes) => response
    }),

    createTeamMember: builder.mutation<
      CreateTeamMemberRes,
      CreateTeamMemberReq
    >({
      query: ({ payload, company_id }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${company_id}/team-member`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: res => (res ? [ApiTags.teamMember] : []),
      transformResponse: (response: CreateTeamMemberRes) => response
    }),
    updateTeamMember: builder.mutation<
      CreateTeamMemberRes,
      UpdateTeamMemberReq
    >({
      query: ({ payload, company_id, member_id }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${company_id}/team-member/${member_id}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: res => (res ? [ApiTags.teamMember] : []),

      transformResponse: (response: CreateTeamMemberRes) => response
    }),

    getInvestor: builder.query<
      {
        data: Investor[];
        limit: number;
        offset: number;
        max_order: number;
        total_count: number;
      },
      GetInvestorReq
    >({
      query: ({
        companyId,
        payload: { search, limit, offset, roundId, type }
      }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/investor?${
          limit ? `&limit=${limit}` : ''
        }${offset !== undefined ? `&offset=${offset}` : ''}${
          search ? `&search=${encodeURIComponent(search)}` : ''
        }${roundId ? `&round-id=${roundId}` : ''}&sort=created_at&order=asc${
          type ? `&type=${type}` : ''
        }${companyId ? `&company-id=${companyId}` : ''}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.investor],
      transformResponse: (
        response: GetInvestorRes
      ): {
        data: Investor[];
        limit: number;
        offset: number;
        max_order: number;
        total_count: number;
      } => {
        return {
          ...response.result,
          data: response.result.data ?? []
        };
      }
    }),
    createInvestor: builder.mutation<CreateInvestorRes, CreateInvestorReq>({
      query: ({ companyId, payload }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${companyId}/investor`,
        method: 'POST',
        body: payload
      }),
      invalidatesTags: res => (res ? [ApiTags.investor] : []),
      transformResponse: (response: CreateInvestorRes) => response
    }),
    updateInvestor: builder.mutation<UpdateInvestorRes, UpdateInvestorReq>({
      query: ({ companyId, payload, investorId }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${companyId}/investor/${investorId}`,
        method: 'PUT',
        body: payload
      }),
      invalidatesTags: res => (res ? [ApiTags.investor] : []),
      transformResponse: (response: UpdateInvestorRes) => response
    }),
    deleteCompanyInvestor: builder.mutation<
      GenericApiResponse<string>,
      DeleteInvestorReq
    >({
      query: ({ companyId, investorId }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${companyId}/company-institutional-investor/${investorId}`,
        method: 'DELETE'
      }),
      invalidatesTags: res => (res ? [ApiTags.investor] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    getTeamMembers: builder.query<TeamMemberRes, TeamMemberListReq>({
      query: ({ companyId, payload: { search, limit, offset } }) => ({
        url: `${
          process.env.REACT_APP_BASE_API_VERSION
        }/support/company/${companyId}/team-member?limit=${limit}&offset=${offset}${
          search ? `&search=${encodeURIComponent(search)}` : ''
        }&sort=created_at&order=asc`,
        method: 'GET'
      }),
      providesTags: [ApiTags.teamMember],
      transformResponse: (response: {
        status: string;
        result: TeamMemberRes;
      }) => response.result
    }),
    addRound: builder.mutation<AddRoundRes, AddRoundReq>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/round`,
        method: 'POST',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.rounds] : []),
      transformResponse: (response: AddRoundRes) => response
    }),
    updateRound: builder.mutation<AddRoundRes, UpdateRoundReq>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/round/${payload.round_id}`,
        method: 'PUT',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.rounds] : []),
      transformResponse: (response: AddRoundRes) => response
    }),
    addValuation: builder.mutation<AddValuationRes, AddValuationReq>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/valuation`,
        method: 'POST',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.rounds] : []),
      transformResponse: (response: AddValuationRes) => response
    }),
    updateValuation: builder.mutation<AddValuationRes, UpdateValuationReq>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/valuation/${payload.valuation_id}`,
        method: 'PUT',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.rounds] : []),
      transformResponse: (response: AddValuationRes) => response
    }),
    getRounds: builder.query<GetRoundsRes, { company_id: string }>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}`,
        method: 'GET'
      }),
      providesTags: [ApiTags.rounds],
      transformResponse: (response: { status: string; result: GetRoundsRes }) =>
        response.result
    }),
    deleteRound: builder.mutation<
      GenericApiResponse<string>,
      { company_id: string; round_id: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/company-round/${payload.round_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.rounds],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteValuation: builder.mutation<
      GenericApiResponse<string>,
      { company_id: string; valuation_id: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/company-valuation/${payload.valuation_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.rounds],
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    linkSector: builder.mutation<
      GenericApiResponse<string>,
      { company_id: string; sector_ids: string[] }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/sector`,
        method: 'POST',
        body: {
          sector_ids: payload.sector_ids
        }
      }),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    unLinkSector: builder.mutation<
      GenericApiResponse<string>,
      { company_id: string; sector_ids: string[] }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/sectors`,
        method: 'POST',
        body: {
          ids: payload.sector_ids
        }
      }),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteTeamMember: builder.mutation<
      GenericApiResponse<string>,
      { companyId: string; teamMemberId: string }
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.companyId}/company-team-member/${payload.teamMemberId}`,
        method: 'DELETE'
      }),
      invalidatesTags: [ApiTags.teamMember]
    }),
    addProduct: builder.mutation<GenericApiResponse<string>, AddProductReq>({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/product`,
        method: 'POST',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    updateProduct: builder.mutation<
      GenericApiResponse<string>,
      UpdateProductReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/product/${payload.product_id}`,
        method: 'PUT',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    addProductAsset: builder.mutation<
      GenericApiResponse<string>,
      AddProductAssetReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/product/${payload.product_id}/asset`,
        method: 'POST',
        body: payload.payload
      }),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),

    deleteProductAsset: builder.mutation<
      GenericApiResponse<string>,
      deleteProductAssetReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/product/${payload.product_id}/asset/${payload.asset_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    addCompetitor: builder.mutation<
      GenericApiResponse<string>,
      addCompetitorReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/competitor`,
        method: 'POST',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteCompetitor: builder.mutation<
      GenericApiResponse<string>,
      deleteCompetitorReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/competitor/${payload.competitor_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    updateCompetitor: builder.mutation<
      GenericApiResponse<string>,
      updateCompetitorReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/competitor/${payload.competitor_id}`,
        method: 'PUT',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    deleteProduct: builder.mutation<
      GenericApiResponse<string>,
      deleteProductReq
    >({
      query: ({ company_id, product_id }) => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${company_id}/product/${product_id}`,
        method: 'DELETE'
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    }),
    updatePreIPOCompanyTerms: builder.mutation<
      GenericApiResponse<string>,
      addCompanyTermsReq
    >({
      query: payload => ({
        url: `${process.env.REACT_APP_BASE_API_VERSION}/support/company/${payload.company_id}/terms`,
        method: 'PATCH',
        body: payload.payload
      }),
      invalidatesTags: res => (res ? [ApiTags.getCompany] : []),
      transformResponse: (response: GenericApiResponse<string>) => response
    })
  })
});

export const {
  useCreateTeamMemberMutation,
  useLazyGetTeamMembersQuery,
  useUpdateTeamMemberMutation,
  useAddValuationMutation,
  useUpdateRoundMutation,
  useAddRoundMutation,
  useGetCompanyQuery,
  useLazyGetCompanyQuery,
  useCreateCompanyMutation,
  useUpdateCompanyMutation,
  useCreateCompanyAddressMutation,
  useUpdateCompanyAddressMutation,
  useCreateCompanyEmailDomainMutation,
  useBulkDeleteCompanyEmailDomainsMutation,
  useGetRoundsQuery,
  useUpdateValuationMutation,
  useDeleteRoundMutation,
  useDeleteValuationMutation,
  useLinkSectorMutation,
  useUnLinkSectorMutation,
  useGetInvestorQuery,
  useLazyGetInvestorQuery,
  useCreateInvestorMutation,
  useUpdateInvestorMutation,
  useDeleteCompanyInvestorMutation,
  useGetCompanyConfigurationQuery,
  useCreateCompanyConfigurationMutation,
  useUpdateCompanyConfigurationMutation,
  useDeleteTeamMemberMutation,
  useAddProductMutation,
  useUpdateProductMutation,
  useAddProductAssetMutation,
  useDeleteProductAssetMutation,
  useAddCompetitorMutation,
  useDeleteCompetitorMutation,
  useUpdateCompetitorMutation,
  useDeleteProductMutation,
  useUpdatePreIPOCompanyTermsMutation
} = CreateCompanyApi;
